import React, { useEffect, useState } from "react";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import { Panel, PanelHeader } from "../../components/panel/panel.jsx";
import Button from "../../components/Button.tsx";
import Loader from "../../components/Loader.tsx";
import { useNavigate, useParams } from "react-router-dom";
import { notification } from "../../utils/Notifications.tsx";
import { getMissingBoxById, updateMissingBox, uploadFilesToMissingBox, deleteMissingBoxFile, getMissingBoxByPartner } from "../../services/missing-box-partner.service.ts";
import { useFormik } from "formik";
import * as Yup from "yup";
import { readBranches } from "../../services/branch.service.ts";
import { getAllActivePartners, fetchPartner } from "../../services/partner.service.ts";
import S3Service from "../../services/s3.service.ts";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import dayjs from "dayjs";
import { DatePicker, ConfigProvider } from "antd";
import esES from "antd/es/locale/es_ES";
import "dayjs/locale/es";
import { getCurrencyFormat, parseCurrency } from "../../utils/index.ts";
dayjs.locale("es");

const EditMissingBox = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [profileUrl, setProfileUrl] = useState("");
  const [missingBox, setMissingBox] = useState(null);
  const [fortnightsCount, setFortnightsCount] = useState(0);
  const [totalInput, setTotalInput] = useState("");
  const [isEditingTotal, setIsEditingTotal] = useState(false);
  const [newFiles, setNewFiles] = useState([]);
  const [activeTab, setActiveTab] = useState('info');
  const [partnerHistory, setPartnerHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  useAppSettings();

  const validationSchema = Yup.object().shape({
    branchId: Yup.number().required("Sucursal requerida"),
    partnerId: Yup.number().required("Colaborador requerido"),
    incidentDate: Yup.number().required("Fecha requerida"),
    totalMissingAmount: Yup.number().positive().required("Monto requerido"),
    amountPerPeriod: Yup.number().positive().required("Monto requerido"),
    startPeriodDate: Yup.number().required("Inicio requerido"),
    endPeriodDate: Yup.number().required("Fin requerido"),
    note: Yup.string().optional(),
  });

  const formik = useFormik({
    validationSchema,
    initialValues: {
      branchId: "",
      partnerId: "",
      incidentDate: null,
      totalMissingAmount: "",
      amountPerPeriod: "",
      startPeriodDate: null,
      endPeriodDate: null,
      note: "",
    },
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const payload: any = {
          branchId: Number(values.branchId),
          partnerId: Number(values.partnerId),
          totalMissingAmount: Number(values.totalMissingAmount),
          amountPerPeriod: Number(values.amountPerPeriod),
          note: values.note || undefined,
        };
        payload.fortnights = fortnightsCount || 0;
        if (typeof values.incidentDate === 'number') payload.incidentDate = values.incidentDate;
        if (typeof values.startPeriodDate === 'number') payload.startPeriodDate = values.startPeriodDate;
        if (typeof values.endPeriodDate === 'number') payload.endPeriodDate = values.endPeriodDate;

        await updateMissingBox(Number(id), payload);
        notification("Éxito", "Faltante en caja actualizado correctamente", "success");
        navigate("/faltantes-caja");
      } catch (e: any) {
        notification("Error", e?.message || "No se pudo actualizar", "danger");
      } finally {
        setLoading(false);
      }
    },
  });

  const loadPartnerInfo = async (partnerId: number) => {
    try {
      setLoading(true);
      const partner = await fetchPartner(partnerId);
      setSelectedPartner(partner);
      const curp = partner?.person?.curp;
      if (curp) {
        try {
          const file = await S3Service.getFile("PERFIL_" + curp);
          setProfileUrl(file?.url || "");
        } catch {
          setProfileUrl("");
        }
      } else {
        setProfileUrl("");
      }
    } catch (error) {
      setSelectedPartner(null);
      setProfileUrl("");
    } finally {
      setLoading(false);
    }
  };

  const loadPartnerHistory = async (partnerId: number) => {
    try {
      setLoadingHistory(true);
      const history = await getMissingBoxByPartner(partnerId);
      setPartnerHistory(history || []);
    } catch (error) {
      console.error("Error loading partner history:", error);
      setPartnerHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const reloadMissingBox = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await getMissingBoxById(Number(id));
      setMissingBox(data);
    } catch (e) {
      // noop
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setInitialLoading(true);
        const [branchesData, missingBoxData] = await Promise.all([
          readBranches(),
          getMissingBoxById(Number(id))
        ]);
        
        setBranches(branchesData);
        setMissingBox(missingBoxData);
        
        const formValues = {
          branchId: missingBoxData.branch?.id?.toString() || "",
          partnerId: missingBoxData.partner?.id?.toString() || "",
          incidentDate: missingBoxData.incidentDate,
          totalMissingAmount: (missingBoxData.totalMissingAmount ?? "").toString(),
          amountPerPeriod: (missingBoxData.amountPerPeriod ?? "").toString(),
          startPeriodDate: missingBoxData.startPeriodDate,
          endPeriodDate: missingBoxData.endPeriodDate,
          note: missingBoxData.note || "",
        } as any;
        formik.setValues(formValues);

        // Calcular quincenas iniciales con las fechas existentes
        if (missingBoxData.startPeriodDate && missingBoxData.endPeriodDate) {
          const ns = normalizeStartToFortnight(dayjs.unix(missingBoxData.startPeriodDate));
          const ne = normalizeEndToFortnight(dayjs.unix(missingBoxData.endPeriodDate));
          const count = countFortnights(ns, ne);
          setFortnightsCount(count);
        } else {
          setFortnightsCount(0);
        }

        // Cargar info de partner seleccionado
        if (missingBoxData.partner?.id) {
          await loadPartnerInfo(missingBoxData.partner.id);
          await loadPartnerHistory(missingBoxData.partner.id);
        }

        const partnersData = await getAllActivePartners();
        setPartners(partnersData || []);

      } catch (error) {
        console.error("Error fetching data:", error);
        notification("Error", "No se pudo cargar los datos", "danger");
        navigate("/faltantes-caja");
      } finally {
        setInitialLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  useEffect(() => {
    if (!isEditingTotal) {
      const val = formik.values.totalMissingAmount as any;
      if (val === null || val === undefined || val === "") {
        setTotalInput("");
      } else {
        setTotalInput(getCurrencyFormat(Number(val)));
      }
    }
  }, [formik.values.totalMissingAmount, isEditingTotal]);

  // Recalcular monto por quincena al cambiar total o número de quincenas
  useEffect(() => {
    recalcAmountPerPeriod(formik.values.totalMissingAmount as any, fortnightsCount);
  }, [formik.values.totalMissingAmount, fortnightsCount]);

  // Helpers quincenas (mismo comportamiento que en NewMissingBox)
  const getMonthEnd = (d: any) => dayjs(d).endOf("month").date();
  const normalizeStartToFortnight = (d: any) => {
    const date = dayjs(d);
    const day = date.date();
    return day <= 15 ? date.startOf("month") : date.date(16);
  };
  const normalizeEndToFortnight = (d: any) => {
    const date = dayjs(d);
    const day = date.date();
    return day <= 15 ? date.date(15) : date.date(getMonthEnd(date));
  };
  const countFortnights = (start: any, end: any) => {
    let s = normalizeStartToFortnight(start);
    const e = normalizeEndToFortnight(end);
    if (s.isAfter(e, "day")) return 0;
    let count = 0;
    while (!s.isAfter(e, "day")) {
      const currentEnd = s.date() <= 15 ? s.date(15) : s.date(getMonthEnd(s));
      count += 1;
      s = currentEnd.add(1, "day");
      s = normalizeStartToFortnight(s);
    }
    return count;
  };

  const recalcAmountPerPeriod = (totalStr: string | number, count: number) => {
    const total = Number(totalStr);
    if (!count || !isFinite(total) || total <= 0) {
      formik.setFieldValue("amountPerPeriod", "");
      return;
    }
    const per = total / count;
    formik.setFieldValue("amountPerPeriod", per.toFixed(2));
  };

  const displayCurrency = (value: any) => {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(value);
    if (!isFinite(num)) return "";
    return getCurrencyFormat(num);
  };

  const errorFor = (name: string) => formik.touched[name as any] && formik.errors[name as any] ? (
    <div className="invalid-feedback" style={{ display: 'block' }}>{formik.errors[name as any] as any}</div>
  ) : null;

  const handleIncidentDateChange = (date: any) => {
    formik.setFieldValue('incidentDate', date ? date.unix() : null);
  };

  const handlePeriodDateChange = (range: any) => {
    const [start, end] = range || [];
    if (!start || !end) {
      setFortnightsCount(0);
      formik.setFieldValue('startPeriodDate', null);
      formik.setFieldValue('endPeriodDate', null);
      return;
    }
    const ns = normalizeStartToFortnight(start);
    const ne = normalizeEndToFortnight(end);
    const count = countFortnights(ns, ne);
    setFortnightsCount(count);
    formik.setFieldValue('startPeriodDate', ns.unix());
    formik.setFieldValue('endPeriodDate', ne.unix());
    recalcAmountPerPeriod(formik.values.totalMissingAmount, count);
  };

  const handlePartnerChange = async (e) => {
    formik.handleChange(e);
    const value = e.target.value;
    if (value) {
      await loadPartnerInfo(Number(value));
      await loadPartnerHistory(Number(value));
    }
  };

  if (initialLoading) {
    return (
      <Layout>
        <div>
          <Loader isLoading={true} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div>
        <Title baseTitle="Faltantes en caja" basePath="/faltantes-caja" title="Editar faltante en caja" />

        <div className="d-flex flex-column flex-lg-row gap-4 m-4">
          <div className="flex-lg-grow-0" style={{ width: "30%" }}>
            <div className="card border-0 rounded h-100 shadow-sm">
              <div className="card-body">
                {/* Tabs */}
                <div className="d-flex mb-3">
                  <button
                    className={`btn btn-sm flex-fill me-2 ${activeTab === 'info' ? 'btn-azul' : 'btn-outline-azul'}`}
                    onClick={() => setActiveTab('info')}
                  >
                    <i className="fas fa-user me-1"></i>
                    Información
                  </button>
                  <button
                    className={`btn btn-sm flex-fill ${activeTab === 'history' ? 'btn-azul' : 'btn-outline-azul'}`}
                    onClick={() => setActiveTab('history')}
                  >
                    <i className="fas fa-history me-1"></i>
                    Historial
                  </button>
                </div>

                {/* Tab Content */}
                {activeTab === 'info' && (
                  <div className="text-center">
                    <h5 className="mb-3 text-azul">Información del colaborador</h5>
                {loading ? (
                  <div className="d-flex flex-column align-items-center py-3">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Cargando...</span>
                    </div>
                    <div className="text-muted small mt-2">Cargando colaborador...</div>
                  </div>
                ) : selectedPartner ? (
                  <>
                    <div className="mb-3">
                      <img
                        src={profileUrl || "/assets/img/avatar.jpg"}
                        alt="Foto de perfil"
                        className="rounded-circle shadow"
                        style={{ width: "100px", height: "100px", objectFit: "cover", border: "4px solid #f8f9fa" }}
                        onError={(e: any) => {
                          e.target.src = "/assets/img/avatar.jpg";
                        }}
                      />
                    </div>
                    <div className="text-center">
                      <h6 className="mb-1 fw-bold fs-4 text-dark">
                        {toCamelCase(`${selectedPartner?.person?.firstName || ""} ${selectedPartner?.person?.lastName || ""} ${selectedPartner?.person?.secondLastName || ""}`)}
                      </h6>
                      <div className="text-muted small mb-1">Puesto</div>
                      <div className="fw-semibold fs-6 text-dark">{selectedPartner?.candidate?.vacancy?.position?.name || "-"}</div>
                      <div className="text-muted small mt-3 mb-1">Sucursal</div>
                      <div className="fw-semibold fs-6 text-dark">{selectedPartner?.candidate?.branch?.name || "-"}</div>
                    </div>
                    <div className="alert alert-info mt-3 text-center">
                      <i className="fa fa-info-circle"></i> Se notificará al chilito al guardar este faltante en caja
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-3">
                      <img
                        src={"/assets/img/avatar.jpg"}
                        alt="Foto de perfil"
                        className="rounded-circle shadow"
                        style={{ width: "100px", height: "100px", objectFit: "cover", border: "4px solid #f8f9fa" }}
                      />
                    </div>
                    <div className="text-center text-muted small mb-3">Selecciona un colaborador para ver su información</div>
                    <div className="alert alert-info mb-2 text-start">Rellena los campos para editar la deducción de faltante en caja</div>
                    <div className="alert alert-warning mb-0 text-start">El cálculo del monto por quincena se realizará automáticamente de acuerdo al periodo seleccionado</div>
                  </>
                )}
                  </div>
                )}

                {/* Historial Tab */}
                {activeTab === 'history' && (
                  <div>
                    <h5 className="mb-3 text-azul">Historial de faltantes</h5>
                    {loadingHistory ? (
                      <div className="d-flex flex-column align-items-center py-3">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Cargando...</span>
                        </div>
                        <div className="text-muted small mt-2">Cargando historial...</div>
                      </div>
                    ) : partnerHistory.length > 0 ? (
                      <div className="overflow-auto" style={{ maxHeight: '400px' }}>
                        {partnerHistory.map((item: any, index: number) => (
                          <div key={item.id || index} className="card mb-2 border-light shadow-sm">
                            <div className="card-body p-3">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div className="fw-semibold text-dark">
                                  {getCurrencyFormat(item.totalMissingAmount)}
                                </div>
                                <span className={`badge ${item.status === 'pending' ? 'bg-warning' : item.status === 'approved' ? 'bg-success' : 'bg-danger'}`}>
                                  {item.status === 'pending' ? 'Pendiente' : item.status === 'approved' ? 'Aprobado' : 'Rechazado'}
                                </span>
                              </div>
                              <div className="text-muted small mb-1">
                                <i className="fas fa-calendar me-1"></i>
                                {dayjs.unix(item.incidentDate).format('DD/MM/YYYY')}
                              </div>
                              <div className="text-muted small mb-1">
                                <i className="fas fa-building me-1"></i>
                                {item.branch?.name || '-'}
                              </div>
                              {item.note && (
                                <div className="text-muted small">
                                  <i className="fas fa-sticky-note me-1"></i>
                                  {item.note}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-muted py-4">
                        <i className="fas fa-inbox fa-2x mb-2"></i>
                        <div>No hay historial de faltantes</div>
                        <small>Este colaborador no tiene registros previos</small>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex-lg-grow-0" style={{ width: "70%" }}>
            <div className="card border-0 rounded mb-4">
              <Panel>
                <PanelHeader noButton={true} className="bg-azul">Editar datos del faltante en caja</PanelHeader>
                <div className="p-4">
                  <ConfigProvider locale={esES}>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Colaborador</label>
                        <select className="form-select" name="partnerId" value={formik.values.partnerId} onChange={handlePartnerChange} onBlur={formik.handleBlur}>
                          <option value="">Selecciona un colaborador</option>
                          {partners?.map((p: any) => (
                            <option key={p.id} value={p.id}>{p.person ? `${p.person.firstName} ${p.person.lastName}` : p.id}</option>
                          ))}
                        </select>
                        {errorFor('partnerId')}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Sucursal</label>
                        <select className="form-select" name="branchId" value={formik.values.branchId} onChange={formik.handleChange} onBlur={formik.handleBlur}>
                          <option value="">Selecciona una sucursal</option>
                          {branches?.map((b: any) => (<option key={b.id} value={b.id}>{b.name}</option>))}
                        </select>
                        {errorFor('branchId')}
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Fecha de incidencia</label>
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" value={formik.values.incidentDate ? dayjs.unix(formik.values.incidentDate) : null} onChange={handleIncidentDateChange} />
                        {errorFor('incidentDate')}
                      </div>
                      <div className="col-md-8 mb-3">
                        <label className="form-label">Periodo de descuento</label>
                        <DatePicker.RangePicker
                          style={{ width: '100%' }}
                          format="DD/MM/YYYY"
                          value={formik.values.startPeriodDate && formik.values.endPeriodDate ? [dayjs.unix(formik.values.startPeriodDate), dayjs.unix(formik.values.endPeriodDate)] : null}
                          renderExtraFooter={() => (<div className="text-muted small"><i className="fa fa-info-circle"></i> Selecciona fechas quincenales</div>)}
                          onChange={handlePeriodDateChange}
                        />
                        {errorFor('startPeriodDate')}
                        {errorFor('endPeriodDate')}
                      </div>

                      <div className="col-md-4 mb-3">
                        <label className="form-label">Monto total</label>
                        <input
                          type="text"
                          className="form-control"
                          name="totalMissingAmount"
                          value={isEditingTotal ? totalInput : displayCurrency(formik.values.totalMissingAmount)}
                          onFocus={() => {
                            setIsEditingTotal(true);
                            const current = formik.values.totalMissingAmount;
                            setTotalInput(current === null || current === undefined || current === '' ? '' : String(current));
                          }}
                          onChange={(e) => {
                            const val = e.target.value;
                            setTotalInput(val);
                            const parsed = parseCurrency(val || '');
                            if (isNaN(parsed)) {
                              formik.setFieldValue('totalMissingAmount', '');
                            } else {
                              formik.setFieldValue('totalMissingAmount', parsed);
                            }
                          }}
                          onBlur={(e) => {
                            formik.handleBlur(e);
                            setIsEditingTotal(false);
                            const parsed = parseCurrency(totalInput || '');
                            if (isNaN(parsed)) {
                              setTotalInput('');
                              formik.setFieldValue('totalMissingAmount', '');
                            } else {
                              setTotalInput(getCurrencyFormat(parsed));
                              formik.setFieldValue('totalMissingAmount', parsed);
                            }
                          }}
                        />
                        {errorFor('totalMissingAmount')}
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Monto por quincena</label>
                        <input type="text" className="form-control" name="amountPerPeriod" value={displayCurrency(formik.values.amountPerPeriod)} disabled />
                        {errorFor('amountPerPeriod')}
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Quincenas</label>
                        <input type="number" className="form-control" value={fortnightsCount || 0} disabled />
                      </div>

                      <div className="col-md-12 mb-3">
                        <label className="form-label">Nota</label>
                        <textarea className="form-control" rows={3} name="note" value={formik.values.note} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                      </div>
                    </div>
                  </ConfigProvider>
                </div>
              </Panel>
            </div>
            <div className="card border-0 rounded">
              <Panel>
                <PanelHeader noButton={true} className="bg-azul">Archivos del faltante</PanelHeader>
                <div className="p-4">
                  {/* Uploader */}
                  <div
                    className="mb-4 border-dashed text-center p-4"
                    style={{ border: "2px dashed #334155", borderRadius: "12px", cursor: "pointer", backgroundColor: "#f8f9fa" }}
                    onDrop={async (e) => {
                      e.preventDefault();
                      const files = Array.from(e.dataTransfer.files || []);
                      setNewFiles((prev) => [...prev, ...files as any]);
                    }}
                    onDragOver={(e) => e.preventDefault()}
                  >
                    <h5 className="text-dark">Arrastra y suelta archivos aquí o selecciona archivos</h5>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        setNewFiles((prev) => [...prev, ...files as any]);
                      }}
                      className="d-none"
                      id="mb-edit-file-upload"
                      accept=".png,.jpg,.jpeg,.pdf"
                    />
                    <label htmlFor="mb-edit-file-upload" className="btn btn-dark">Seleccionar archivos</label>
                  </div>

                  {(newFiles.length > 0) && (
                    <div className="col-12 mb-3">
                      <h6>Nuevos archivos:</h6>
                      <div className="row">
                        {newFiles.map((file: any, index: number) => (
                          <div key={index} className="col-md-3 mb-3">
                            <div className="card shadow-sm border-light rounded position-relative" style={{ cursor: 'pointer' }}>
                              <div className="card-body text-center">
                                <div className="mb-2">
                                  {file.type && file.type.match(/(png|jpg|jpeg)$/) ? (
                                    <img src={URL.createObjectURL(file)} alt={file.name} className="img-fluid rounded" style={{ maxHeight: '150px', objectFit: 'cover' }} />
                                  ) : file.type && file.type.match(/(pdf)$/) ? (
                                    <div>
                                      <p>PDF seleccionado</p>
                                    </div>
                                  ) : (
                                    <p>Archivo</p>
                                  )}
                                </div>
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => {
                                    setNewFiles((prev) => prev.filter((_: any, i: number) => i !== index));
                                  }}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="text-end">
                        <Button
                          title="Subir archivos"
                          variant="azul"
                          type="button"
                          onClick={async () => {
                            if (!id || newFiles.length === 0) return;
                            try {
                              setLoading(true);
                              await uploadFilesToMissingBox(Number(id), newFiles as any);
                              setNewFiles([]);
                              await reloadMissingBox();
                              notification('Éxito', 'Archivos subidos', 'success');
                            } catch (e: any) {
                              notification('Error', e?.message || 'No se pudieron subir los archivos', 'danger');
                            } finally {
                              setLoading(false);
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Existing files */}
                  {missingBox?.missingBoxFiles?.length > 0 ? (
                    <div className="col-12">
                      <h6>Archivos existentes:</h6>
                      <div className="row">
                        {missingBox.missingBoxFiles.map((f: any) => (
                          <div key={f.id} className="col-md-3 mb-3">
                            <div className="card shadow-sm border-light rounded position-relative" style={{ cursor: 'pointer' }}>
                              <div className="card-body text-center">
                                <div className="mb-2">
                                  {f.file?.extension?.match(/(png|jpg|jpeg)$/) ? (
                                    <img src={f.file.url} alt={f.file.name} className="img-fluid rounded" style={{ maxHeight: '150px', objectFit: 'cover' }} />
                                  ) : f.file?.extension?.match(/(pdf)$/) ? (
                                    <div>
                                      <a href={f.file.url} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">Ver PDF</a>
                                    </div>
                                  ) : (
                                    <a href={f.file?.url} target="_blank" rel="noopener noreferrer">Descargar</a>
                                  )}
                                </div>
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={async () => {
                                    try {
                                      setLoading(true);
                                      await deleteMissingBoxFile(f.id);
                                      await reloadMissingBox();
                                      notification('Éxito', 'Archivo eliminado', 'success');
                                    } catch (e: any) {
                                      notification('Error', e?.message || 'No se pudo eliminar', 'danger');
                                    } finally {
                                      setLoading(false);
                                    }
                                  }}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-muted">No hay archivos adjuntos.</div>
                  )}
                </div>
              </Panel>
            </div>
          </div>
        </div>

        <div className="card border-0 m-4 rounded p-3">
          <div className="row justify-content-end">
            <div className="col-md-3">
              <Button title="Cancelar" variant="secondary" onClick={() => navigate("/faltantes-caja")} type="button" />
            </div>
            <div className="col-md-3">
              <Button title="Actualizar" variant="azul" onClick={() => formik.handleSubmit()} type="button" />
            </div>
          </div>
        </div>

        <Loader isLoading={loading} />
      </div>
    </Layout>
  );
};

export default EditMissingBox;
